import { useMutation } from '@apollo/client';
import { RestoreOrgUserDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useRestoreUserTRPC } from './useRestoreUserTRPC';

/**
 * Restore a previously archived non-SCIM org user, dual-routing between the
 * tRPC stack and the legacy Hasura/Apollo path based on the `trpc` feature flag.
 *
 * The Apollo path lives inline here (rather than a separate `*GraphQL` hook) so
 * the auto-migration tracker detects `RestoreOrgUserDocument` in this flag-gated
 * bridge file — matching the canonical 2-file mutation pattern. Refetch by
 * operation name so the active observable matches regardless of variables.
 */
export const useRestoreUser = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [restoreUserGraphQL, graphqlState] = useMutation(
    RestoreOrgUserDocument,
    {
      refetchQueries: ['GetAuthUsers'],
      awaitRefetchQueries: true,
      update: (cache) => {
        evictField(cache, 'auth_user');
        evictField(cache, 'auth_user_aggregate');
      },
    }
  );

  const trpc = useRestoreUserTRPC();

  const restoreUser = async (userId: string) => {
    if (trpcEnabled) {
      return trpc.restoreUser(userId);
    }

    const result = await restoreUserGraphQL({ variables: { userId } });
    if (!result.data?.restore_org_user) {
      throw new Error('Restore user mutation returned no data');
    }

    return result.data.restore_org_user;
  };

  return {
    restoreUser,
    loading: trpcEnabled ? trpc.loading : graphqlState.loading,
    error: trpcEnabled ? trpc.error : graphqlState.error,
  };
};
